import { useState } from "react"
import type { Control } from "react-hook-form"

import { Loader2,Trash2 } from "lucide-react"
import { Card,CardContent,CardHeader,CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { FormControl,FormField,FormItem,FormLabel } from "@/components/ui/form"
import { Switch } from "@/components/ui/switch"
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog"
import { cn } from "@/lib/utils"


import type { SaleConfigFormValues } from "./sale-config-utils"

interface BehaviorFlagsCardProps {
  control: Control<SaleConfigFormValues>
}

interface FormActionBarProps {
  saleConfigId?: string | null
  deletePending: boolean
  savePending: boolean
  onDelete: () => void
  onCancel: () => void
}

export const BehaviorFlagsCard = ({ control }: BehaviorFlagsCardProps) => {
  return (
    <Card>
      <CardHeader className="px-4 py-1.5">
        <CardTitle className="text-base">Behavior Flags</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-6 px-4 pb-4">
        <FormField
          control={control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <FormLabel>Active</FormLabel>
              <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="allowOnlineSale"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <FormLabel>Online Sale</FormLabel>
              <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isDiscountable"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <FormLabel>Discountable</FormLabel>
              <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isDeliverable"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <FormLabel>Deliverable</FormLabel>
              <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isPromo"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <FormLabel>Is Promo</FormLabel>
              <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isSample"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <FormLabel>Is Sample</FormLabel>
              <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  )
}

export const FormActionBar = ({
  saleConfigId,
  deletePending,
  savePending,
  onDelete,
  onCancel,
}: FormActionBarProps) => {
  const [deleteOpen, setDeleteOpen] = useState(false)

  return (
    <div
      className={cn(
        "mt-4 flex sticky bottom-0 gap-3 py-4 pt-4 border-t bg-muted/90 backdrop-blur-md px-6 -mx-6 z-10",
        saleConfigId ? "justify-between" : "justify-end"
      )}
    >
      {saleConfigId && (
        <>
          <Button
            type="button"
            variant="destructive"
            disabled={deletePending}
            className="px-3"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Delete</span>
          </Button>

          <DeleteConfirmationDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            title="Delete Sale Configuration?"
            description="This will permanently delete this sale configuration. This action cannot be undone."
            onConfirm={onDelete}
            confirmLabel="Delete"
            isPending={deletePending}
          />
        </>
      )}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={savePending}>
          {savePending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Configuration
        </Button>
      </div>
    </div>
  )
}

